import React, { useState, useEffect } from 'react';
import { Container, Typography, Card, CardContent, Button, Chip, Box, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';

interface Strand {
  _id: string;
  name: string;
  description: string;
  type: string;
  startDate: string;
  endDate: string;
  eventType: string;
  status: string;
  published: boolean;
  slug: string;
}

const strandRoutes: { [key: string]: string } = {
  'Better Live': '/recommend',
  'Navigator': '/navigator',
  'Ban Bam': '/banbam',
};

const PublicStrandsPage: React.FC = () => {
  const navigate = useNavigate();
  const [strands, setStrands] = useState<Strand[]>([]);
  const [formsMap, setFormsMap] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

useEffect(() => {
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const strandsResponse = await axiosInstance.get<Strand[]>('/api/strands?published=true');
      const publishedStrands = strandsResponse.data;
      console.log('📊 Strands:', publishedStrands);
      setStrands(publishedStrands);

      const formsResponse = await axiosInstance.get('/api/formTemplates');
      const formsData = formsResponse.data;
      console.log('📋 Forms:', formsData);
      
      const formsMapping: Record<string, any> = {};
      formsData.forEach((form: any) => {
        if (form.strand) {
          console.log(`📎 Mapping form "${form.slug}" to strand "${form.strand}"`);
          formsMapping[form.strand] = form;
        }
      });
      console.log('🗺️ Final formsMapping:', formsMapping);
      setFormsMap(formsMapping);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to fetch opportunities. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, []);

  const handleApply = (strand: Strand) => {
    const form = formsMap[strand._id];
    if (form && form.slug) {
      navigate(`/form/${form.slug}`);
    } else {
      // Fallback to old routes
      const route = strandRoutes[strand.name] || '/recommend';
      navigate(route);
    }
  };

  const handleGuidelines = (strand: Strand) => {
    navigate(`/guidelines/strand/${strand.slug}`);
  };

const getRemainingDays = (endDate: string) => {
  const now = new Date();
  const deadline = new Date(endDate);
  deadline.setHours(18, 0, 0, 0); // 6pm local time
  const diffTime = deadline.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

 const RemainingDaysChip = ({ days }: { days: number }) => {
  const isClosed = days <= 0;

  const color =
    days > 30 ? 'success' :
    days > 14 ? 'warning' :
    days > 0 ? 'error' :
    'default';

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 16,
        right: 16,
        width: 92,
        height: 92,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'rgba(0,0,0,0.18)',
        border: `1px solid rgba(255,255,255,0.12)`,
        backdropFilter: 'blur(6px)'
      }}
    >
      <Typography variant="h4" sx={{ lineHeight: 1, fontWeight: 800 }}>
        {isClosed ? '—' : days}
      </Typography>
      <Chip
        label={isClosed ? 'Closed' : 'days left'}
        color={color as 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
        size="small"
        sx={{ mt: 1 }}
      />
    </Box>
  );
};

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  return (
    <Container>
      <Typography variant="h2" gutterBottom>Open Opportunities</Typography>
      {strands.length === 0 ? (
        <Typography>No open opportunities available at the moment.</Typography>
      ) : (
        strands.map((strand) => {
          const remainingDays = getRemainingDays(strand.endDate);
          const hasForm = !!formsMap[strand._id];
           // ADD THIS LOG
  console.log(`🔍 Strand "${strand.name}":`, {
    strandId: strand._id,
    hasForm,
    form: formsMap[strand._id],
    status: strand.status,
    remainingDays
  });

  const isClosed = strand.status !== 'confirmed' || remainingDays <= 0;
          
          return (
<Card
  key={strand._id}
  sx={{
    mb: 3,
    position: 'relative',
    opacity: isClosed ? 0.45 : 1,
    filter: isClosed ? 'grayscale(100%)' : 'none'
  }}
>              <RemainingDaysChip days={remainingDays} />
              <CardContent>
                <Typography variant="h4">{strand.name}</Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {strand.type} | {new Date(strand.startDate).toLocaleDateString()} - {new Date(strand.endDate).toLocaleDateString()}
                </Typography>
                <Typography variant="body1" paragraph>
                  {strand.description}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Chip 
                    label={strand.status === 'confirmed' ? "Open for Submissions" : "Closed"} 
                    color={strand.status === 'confirmed' ? "success" : "error"} 
                  />
                  {strand.status === 'confirmed' && (
                    <Chip 
                      label={`Deadline: ${new Date(strand.endDate).toLocaleDateString()} 18:00`}
                      color="primary" 
                      sx={{ ml: 1 }} 
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Button 
                    variant="contained" 
                    color="primary"
                    onClick={() => handleApply(strand)}
                    disabled={!hasForm || strand.status !== 'confirmed' || remainingDays <= 0}
                  >
                    Apply Now
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleGuidelines(strand)}
                  >
                    Guidelines
                  </Button>
                </Box>
              </CardContent>
            </Card>
          );
        })
      )}
    </Container>
  );
};

export default PublicStrandsPage;